import { atom, useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { GROUPS_OPEN_BY_DEFAULT } from './groupModel';

/**
 * WHICH GROUPS ARE OPEN, AND WHICH ONE HAS ASKED FOR ALL ITS ROWS
 * (`docs/decisions/group-by-aggregation.md` D17, D19, D22).
 *
 * BOTH ARE TRANSIENT ON PURPOSE, and D17 makes that a rule rather than a shortcut:
 * PERSIST WHAT THE SETTINGS MENU IMPLIES YOU PERSIST. Collapse is a control on the group
 * header, like a disclosure triangle, which is what users already expect not to travel -
 * and neither of these appears in the view settings, so nothing promises otherwise.
 * Twenty's own settings menu advertises a persistence it does not have, and the lesson
 * from that is this file's whole shape: a Jotai atom keyed by view, dead on reload.
 *
 * KEYED BY VIEW rather than held in the table, because switching to the board and back
 * UNMOUNTS the table - that is exactly what D20's state lift was about - and a group list
 * that reset itself on a mode switch would be a control nobody could rely on within one
 * session. It is one module-level atom rather than a store, so two tables on two objects
 * cannot collide and neither can two views of one object.
 *
 * TWO AXES, and they are different questions:
 *  - OPEN is the disclosure. Groups past the first ten render CLOSED, because there is no
 *    virtualization in this package and D20's caps allow 200 groups x 50 rows; the DOM
 *    cost is then bounded by what somebody has actually opened (D22).
 *  - EXPANDED is D19's "show all the rows in this group". An open group shows the SEEDED
 *    PREVIEW the grouped read returned; expanding DISCARDS those rows and mounts the
 *    ordinary infinite list from page 1, because the row statement ranks with
 *    `row_number()` and emits no cursor, so a "show more" starts at page 1 whatever we do
 *    and the only question was whether the preview stays above it and appears twice.
 */
interface GroupDisplayState {
  /** Explicit overrides only; a group with no entry falls back to its position. */
  readonly open: Readonly<Record<string, boolean>>;
  readonly expanded: Readonly<Record<string, true>>;
}

const EMPTY: GroupDisplayState = { open: {}, expanded: {} };

const groupDisplayAtom = atom<Readonly<Record<string, GroupDisplayState>>>({});

export interface GroupDisplay {
  /** Whether this group shows its rows at all. Position decides until somebody clicks. */
  readonly isOpen: (encodedKey: string, index: number) => boolean;
  readonly toggleOpen: (encodedKey: string, index: number) => void;
  /** Whether this group has left its preview behind for the ordinary list (D19). */
  readonly isExpanded: (encodedKey: string) => boolean;
  readonly expand: (encodedKey: string) => void;
}

export function useGroupDisplay(viewKey: string): GroupDisplay {
  const [byView, setByView] = useAtom(groupDisplayAtom);
  const state = byView[viewKey] ?? EMPTY;

  const isOpen = useCallback(
    (encodedKey: string, index: number) => state.open[encodedKey] ?? index < GROUPS_OPEN_BY_DEFAULT,
    [state],
  );

  const toggleOpen = useCallback(
    (encodedKey: string, index: number) => {
      setByView((current) => {
        const view = current[viewKey] ?? EMPTY;
        const now = view.open[encodedKey] ?? index < GROUPS_OPEN_BY_DEFAULT;
        return {
          ...current,
          [viewKey]: { ...view, open: { ...view.open, [encodedKey]: !now } },
        };
      });
    },
    [setByView, viewKey],
  );

  const isExpanded = useCallback(
    (encodedKey: string) => state.expanded[encodedKey] === true,
    [state],
  );

  /**
   * ONE WAY, deliberately: a group that has mounted the ordinary list does not fold back
   * into its preview. The preview came from a snapshot the grouped read held and the list
   * came from a later one, so offering the trip back would offer two different answers to
   * "the first twenty records here" and let the user pick.
   */
  const expand = useCallback(
    (encodedKey: string) => {
      setByView((current) => {
        const view = current[viewKey] ?? EMPTY;
        return {
          ...current,
          [viewKey]: { ...view, expanded: { ...view.expanded, [encodedKey]: true } },
        };
      });
    },
    [setByView, viewKey],
  );

  return useMemo(
    () => ({ isOpen, toggleOpen, isExpanded, expand }),
    [isOpen, toggleOpen, isExpanded, expand],
  );
}
